import { useState, useEffect } from 'react';
import {
    ResponsiveContainer,
    Tooltip,
    LineChart,
    CartesianGrid,
    Line,
    XAxis,
    YAxis,
    Area,
    AreaChart
} from 'recharts';
import {
    Box,
    Select,
    MenuItem,
    FormControl,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    Paper,
    Chip,
    CircularProgress
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import ErrorHandler from '@Root/ErrorHandler';
import LabelViewExtendService from '@Helpers/URLHelpers/WeboutService/views/LabelViewExtendService';
import './LabelViewCreatedOverTime.scss';

export default function LabelViewCreatedOverTime(props) {
    const [, setCursor] = useState('auto');
    const [time, setTime] = useState(3);
    const [filters, setFilters] = useState({
        labelViewFilter: {
            labelId: props?.labelData?.id,
            registryType: props?.labelData?.registryType,
            getLabelDateFrom: 0,
            getLabelDateTo: Date.now(),
        },
    });
    const [data, setData] = useState([]);
    const [isCumulative, setIsCumulative] = useState('cumulative');
    const [isLoading, setIsLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const labelViewExtendService = new LabelViewExtendService();
    const errorHandler = new ErrorHandler();
    const className = 'labelViewCreatedOverTime';

    const calculateMonths = (monthsAgo) => {
        const toDate = new Date();
        toDate.setDate(1);
        toDate.setHours(0, 0, 0, 0);

        const fromDate = new Date(toDate);
        fromDate.setMonth(toDate.getMonth() - (monthsAgo - 1));

        return {
            from: fromDate.getTime(),
            to: new Date(toDate.getFullYear(), toDate.getMonth() + 1, 0, 23, 59, 59, 999).getTime(),
        };
    };

    const handleChartData = (fromDateMs, dataFromAPI, cumulative) => {
        const fromDate = new Date(fromDateMs);
        const currentDate = new Date();
        const months = [];
        let tempDate = new Date(fromDate);
        while (tempDate <= currentDate) {
            const monthIndex = tempDate.getMonth();
            const year = tempDate.getFullYear();
            const key = `${year}-${monthIndex}`;
            months.push({
                name: tempDate.toLocaleString('default', { month: 'short' }) + ` '${String(year).slice(2)}`,
                value: 0,
                key,
            });
            tempDate.setMonth(tempDate.getMonth() + 1);
        }
        dataFromAPI.forEach(item => {
            const timestamp = typeof item === 'number' ? item : item.timestamp || item.createdAt;
            if (!timestamp) return;
            const date = new Date(Number(timestamp));
            if (isNaN(date)) return;
            const key = `${date.getFullYear()}-${date.getMonth()}`;
            const found = months.find(m => m.key === key);
            if (found) found.value += 1;
        });
        
        let total = 0;
        if (cumulative) {
            for (let i = 1; i < months.length; i++) {
                months[i].value += months[i - 1].value;
            }
            total = months[months.length - 1]?.value || 0;
        } else {
            total = months.reduce((sum, m) => sum + m.value, 0);
        }
        
        setTotalCount(total);
        setData(months);
    };

    const fetchDataAndUpdate = async (selectedMonths, cumulative) => {
        setCursor('wait');
        setIsLoading(true);
        const dateRange = calculateMonths(selectedMonths);
        const updatedFilters = {
            labelViewFilter: {
                labelId: props?.labelData?.id,
                registryType: props?.labelData?.registryType,
                getLabelDateFrom: dateRange.from,
                getLabelDateTo: dateRange.to,
            },
        };

        try {
            const response = await labelViewExtendService.getLabelViewByCreatedOverTime(updatedFilters);
            const responseJson = JSON.parse(response.body);
            setFilters(updatedFilters);
            handleChartData(dateRange.from, responseJson, cumulative === 'cumulative');
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
        setIsLoading(false);
    };

    const handleSelectChange = (e) => {
        const selectedMonths = e.target.value;
        setTime(selectedMonths);
        fetchDataAndUpdate(selectedMonths, isCumulative);
    };

    const handleToggleChange = (event, newValue) => {
        if (newValue !== null) {
            setIsCumulative(newValue);
            fetchDataAndUpdate(time, newValue);
        }
    };

    useEffect(() => {
        renderData();
    }, [props?.labelData?.id]);

    const renderData = () => {
        let filterObj = filters;
        filterObj.labelViewFilter.labelId = props?.labelData?.id;
        filterObj.labelViewFilter.registryType = props?.labelData?.registryType;
        setFilters(filterObj);
        fetchDataAndUpdate(time, 'cumulative');
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Paper className={`${className}__tooltip`} elevation={3}>
                    <Typography variant="caption" className={`${className}__tooltip__label`}>
                        {label}
                    </Typography>
                    <Typography variant="body2" className={`${className}__tooltip__value`}>
                        {payload[0].value} {isCumulative === 'cumulative' ? 'total' : 'created'}
                    </Typography>
                </Paper>
            );
        }
        return null;
    };

    return (
        <div className={className}>
            {/* Header Section */}
            <Box className={`${className}__header`}>
                <Box className={`${className}__header__left`}>
                    <TrendingUpIcon className={`${className}__header__icon`} />
                    <Box>
                        <Typography variant="h6" className={`${className}__header__title`}>
                            Created Over Time
                        </Typography>
                        <Typography variant="caption" className={`${className}__header__subtitle`}>
                            Track creation trends and patterns
                        </Typography>
                    </Box>
                </Box>
                <Box className={`${className}__header__right`}>
                    <Chip
                        icon={<BarChartIcon />}
                        label={`Total: ${totalCount}`}
                        className={`${className}__header__totalChip`}
                        color="primary"
                        variant="outlined"
                    />
                </Box>
            </Box>

            {/* Controls Section */}
            <Box className={`${className}__controls`}>
                <FormControl size="small" className={`${className}__controls__select`}>
                    <Select
                        value={time}
                        onChange={handleSelectChange}
                        disabled={isLoading}
                        className={`${className}__controls__select__input`}
                    >
                        <MenuItem value={3}>Last 3 Months</MenuItem>
                        <MenuItem value={6}>Last 6 Months</MenuItem>
                        <MenuItem value={12}>Last 12 Months</MenuItem>
                    </Select>
                </FormControl>

                <ToggleButtonGroup
                    value={isCumulative}
                    exclusive
                    onChange={handleToggleChange}
                    size="small"
                    className={`${className}__controls__toggle`}
                    disabled={isLoading}
                >
                    <ToggleButton value="cumulative" className={`${className}__controls__toggle__btn`}>
                        <ShowChartIcon fontSize="small" />
                        <span>Cumulative</span>
                    </ToggleButton>
                    <ToggleButton value="period" className={`${className}__controls__toggle__btn`}>
                        <BarChartIcon fontSize="small" />
                        <span>Period</span>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Chart Section */}
            <Box className={`${className}__chartContainer`}>
                {isLoading ? (
                    <Box className={`${className}__loader`}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="textSecondary">
                            Loading chart data...
                        </Typography>
                    </Box>
                ) : data.length === 0 ? (
                    <Box className={`${className}__empty`}>
                        <ShowChartIcon className={`${className}__empty__icon`} />
                        <Typography variant="body2" color="textSecondary">
                            No data available for the selected period
                        </Typography>
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height={280}>
                        {isCumulative === 'cumulative' ? (
                            <AreaChart data={data}>
                                <defs>
                                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="var(--color-primaryBlue)" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="var(--color-primaryBlue)" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-darkBorder)" vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    stroke="var(--color-primaryGrey)"
                                    style={{ fontSize: '0.75rem' }}
                                    tickLine={false}
                                />
                                <YAxis
                                    stroke="var(--color-primaryGrey)"
                                    style={{ fontSize: '0.75rem' }}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke="var(--color-primaryBlue)"
                                    strokeWidth={2}
                                    fill="url(#colorValue)"
                                    animationDuration={800}
                                />
                            </AreaChart>
                        ) : (
                            <LineChart data={data}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-darkBorder)" vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    stroke="var(--color-primaryGrey)"
                                    style={{ fontSize: '0.75rem' }}
                                    tickLine={false}
                                />
                                <YAxis
                                    stroke="var(--color-primaryGrey)"
                                    style={{ fontSize: '0.75rem' }}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke="var(--color-primaryBlue)"
                                    strokeWidth={2}
                                    dot={{ fill: 'var(--color-primaryBlue)', r: 4 }}
                                    activeDot={{ r: 6 }}
                                    animationDuration={800}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                )}
            </Box>
        </div>
    );
}
